import {HttpHeaders} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {EMPTY, filter, Observable} from 'rxjs';
import {take, takeUntil, tap} from 'rxjs/operators';
import {UserRole} from '../../enums/user-role';
import {getGuestStartPageUrl} from '../../models/route-redirects';
import {EchoService} from '../../services/echo.service';
import {destroy$} from '../destroyable';
import {DynamicZyxDaemonService} from '../dynamic-xyz/dynamic-zyx-daemon.service';
import {AuthClientService} from './auth-client.service';

@Injectable()
export class DexAuthClientService extends AuthClientService {

    // It's used in the parent constructor, so it can't be injected right here.
    public destroy$: Observable<any>;

    // It's used in the parent constructor, so it can't be injected right here.
    private dynamicZyxDaemonService: DynamicZyxDaemonService;

    // It's used in the parent constructor, so it can't be injected right here.
    private echoService: EchoService;

    private dynAuthToken: string;

    public override redirectToLogin(): void {
        if (!this.userRole) {
            let url: string;

            const isGuestDashboardEnabled = this.appConfigService.appConfig.isGuestDashboardEnabled;

            if (isGuestDashboardEnabled) {
                url = getGuestStartPageUrl(this.routeRedirects);
            } else {
                url = this.routeRedirects.LOGIN_ROUTE;
            }

            this.router.navigate([url], {
                onSameUrlNavigation: isGuestDashboardEnabled ? 'reload' : undefined
            });
        }
    }

    public override setCredentials(userRole: UserRole, authToken: string, dynAuthToken?: string): void {
        super.setCredentials(userRole, authToken);

        this.setDynAuthToken(dynAuthToken);
    }

    /**
     * [HED-935] Returns auth token prepended with the type of the token:
     * Internal - means it's the session token issued by the app's backend.
     * Bearer - means it's the token issued by Dynamic.
     */
    public override getAuthHeaderValue(authToken?: string): string | null | undefined {
        let result = super.getAuthHeaderValue(authToken);

        if (!result) {
            authToken = this.dynAuthToken;

            if (authToken) {
                result = `Bearer ${authToken}`;
            }
        }

        return result;
    }

    public override getAuthHttpHeaders(headers: HttpHeaders, authToken?: string): HttpHeaders {
        const AUTH_HEADER_NAME = 'Authorization';
        let authHeaders: HttpHeaders = headers;

        if (!headers.has(AUTH_HEADER_NAME)) {
            const authHeaderValue = this.getAuthHeaderValue(authToken);

            if (authHeaderValue) {
                authHeaders = headers.set(AUTH_HEADER_NAME, authHeaderValue);
            }
        }

        return authHeaders;
    }

    protected override internalInitAuthStatus(): Observable<any> {
        let result$: Observable<any>;

        this.initDependencies();

        const authToken: string = this.getToken();
        const userRole: UserRole = this.getUserRoleFromLocalStorage();

        if (authToken && userRole) {
            try {
                this.setCredentials(userRole, authToken);
            } catch (e) {
                this.clearCredentials();
            }

            result$ = EMPTY;
        } else {
            result$ = this.dynamicZyxDaemonService.dynRemote$.pipe(
                filter(Boolean),
                take(1),
                tap(() => {
                    if (this.dynamicZyxDaemonService.getDynAuthToken()) {
                        this.setCredentials(UserRole.TRADER, undefined, this.dynamicZyxDaemonService.getDynAuthToken());

                        this.dynamicZyxDaemonService.getLogInStream().pipe(
                            take(1),
                            tap(() => this.echoService.initializeSendingEchoRequests(
                                this.dynamicZyxDaemonService.getLogOutStream()
                            )),
                            takeUntil(this.destroy$)
                        ).subscribe();
                    } else {
                        this.clearCredentials();
                    }

                    this.listenToDynAuth();
                }),
                takeUntil(this.destroy$)
            );
        }

        return result$;
    }

    protected override clearCredentials(): void {
        super.clearCredentials();

        this.setDynAuthToken(null);
    }

    protected override initCredentials(): void {
        console.error('Not implemented in DEX');
    }

    private setDynAuthToken(dynAuthToken: string): void {
        this.dynAuthToken = dynAuthToken;
    }

    private listenToDynAuth(): void {
        this.dynamicZyxDaemonService.listenToAuthToken().pipe(
            tap(dynAuthToken => {
                if (dynAuthToken) {

                    // Reload the page if the auth happen after the service was initialized.
                    if (dynAuthToken !== this.dynAuthToken) {
                        location.reload();

                        /**
                         * TODO: [DEX] Rework dashboard to not require full page reloading on login/logout.
                         *
                         * Logout takes some additional time to complete login requests to Dynamic API.
                         * As a workaround use a timeout before reloading the page.
                         */
                        setTimeout(() => {
                            location.reload();
                        }, 1000);
                    }
                } else {
                    if (this.dynAuthToken) {

                        /**
                         * TODO: [DEX] Rework dashboard to not require full page reloading on login/logout.
                         *
                         * Logout takes some additional time to complete logout requests to Dynamic API.
                         * As a workaround use a timeout before reloading the page.
                         */
                        this.logout()
                            .finally(() => setTimeout(() => {
                                location.reload();
                            }, 1000));
                    }
                }
            }),
            takeUntil(this.destroy$)
        ).subscribe();
    }

    /**
     * Init dependencies, that are needed in the parent constructor.
     */
    private initDependencies(): void {
        this.dynamicZyxDaemonService = this.injector.get(DynamicZyxDaemonService);
        this.echoService = this.injector.get(EchoService);
        this.destroy$ = destroy$();
    }
}
